import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ChevronRight, Loader2, MessageCircle } from "lucide-react";

import { messagesApi } from "@/api/messages";
import { logger } from "@/utils/logger";

const Inbox = () => {
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchInbox();
  }, []);

  const fetchInbox = async () => {
    try {
      const data = await messagesApi.inbox();
      setThreads(data.threads);
    } catch (error) {
      logger.error("Failed to fetch inbox:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="pt-24 pb-16 px-6 md:px-12 min-h-screen bg-[#050505]">
      <div className="max-w-4xl mx-auto">
        <div className="mb-8">
          <p className="text-xs tracking-[0.3em] uppercase text-[#D4AF37] mb-2">Messages</p>
          <h1 className="text-4xl text-[#EDEDED]">Inbox</h1>
        </div>

        {loading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="w-8 h-8 text-[#D4AF37] animate-spin" />
          </div>
        ) : threads.length === 0 ? (
          <div className="text-center py-20">
            <MessageCircle className="w-16 h-16 text-[#A1A1AA]/30 mx-auto mb-4" />
            <p className="text-[#A1A1AA] text-lg mb-2">No messages yet</p>
            <p className="text-sm text-[#71717A]">Start a conversation by contacting a breeder on a listing.</p>
            <Link to="/" className="btn-secondary text-sm mt-6 inline-block">Browse Listings</Link>
          </div>
        ) : (
          <div className="space-y-3">
            {threads.map((thread) => (
              <button
                key={thread.thread_id}
                onClick={() => navigate(`/messages/${thread.thread_id}`)}
                className="w-full glass-card p-4 flex items-center gap-4 hover:border-[#D4AF37]/30 transition-colors text-left"
                data-testid={`thread-${thread.thread_id}`}
              >
                <img
                  src={thread.listing_photo || "https://via.placeholder.com/60"}
                  alt=""
                  className="w-14 h-14 rounded-lg object-cover flex-shrink-0"
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <p className="text-[#EDEDED] font-medium truncate">
                      {thread.role === 'buyer' ? thread.breeder_name : thread.buyer_name}
                    </p>
                    {thread.unread > 0 && (
                      <span className="bg-[#D4AF37] text-black text-xs px-2 py-0.5 rounded-full">{thread.unread}</span>
                    )}
                  </div>
                  <p className="text-sm text-[#A1A1AA] truncate">{thread.listing_title}</p>
                  <p className="text-sm text-[#71717A] truncate mt-1">{thread.last_message_preview}</p>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="text-xs text-[#71717A]">{new Date(thread.last_message_at).toLocaleDateString()}</p>
                  {thread.status === 'closed' && <span className="text-xs text-red-400">Closed</span>}
                </div>
                <ChevronRight className="w-5 h-5 text-[#A1A1AA] flex-shrink-0" />
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Inbox;
